import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Carousel, CarouselContent, CarouselItem } from "../ui/carousel";
import { BimInfographic } from "../BimInfographic";
import { getApiBase } from "../../lib/apiBase";

const HomeHeroMediaSlide = ({ item }) => (
  <div className="relative mx-auto w-full overflow-hidden rounded-[2rem] border border-[#d9e7d5] bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(240,253,244,0.95)_100%)] shadow-[0_22px_70px_-48px_rgba(15,118,110,0.2)]" data-testid={`admin-home-hero-media-slide-${item.id}`}>
    <img alt={item.filename} className="h-[22rem] w-full object-contain bg-[#f8fcf3] lg:h-[28rem]" data-testid={`admin-home-hero-media-preview-${item.id}`} src={`${getApiBase()}${item.url}`} />
  </div>
);

export const AdminHomeHeroMediaManager = () => {
  const [carouselApi, setCarouselApi] = useState();
  const [activeIndex, setActiveIndex] = useState(0);
  const [mediaItems, setMediaItems] = useState([]);
  const inputRef = useRef(null);

  const fetchMedia = async () => {
    const API = getApiBase();
    const { data } = await axios.get(`${API}/api/admin/home/hero-media`, { withCredentials: true });
    setMediaItems(data);
  };

  useEffect(() => {
    fetchMedia().catch(() => toast.error("Impossible de charger les médias de l’accueil."));
  }, []);

  useEffect(() => {
    if (!carouselApi) return undefined;
    const onSelect = () => setActiveIndex(carouselApi.selectedScrollSnap());
    onSelect();
    carouselApi.on("select", onSelect);
    return () => carouselApi.off("select", onSelect);
  }, [carouselApi]);

  const slides = useMemo(() => [{ id: "locked-home-hero", locked: true }, ...mediaItems.map((item) => ({ ...item, locked: false }))], [mediaItems]);
  const activeSlide = slides[activeIndex] || slides[0];

  const handleUpload = async (files) => {
    if (!files.length) {
      toast.error("Ajoutez au moins un média avant l’envoi.");
      return;
    }
    try {
      const API = getApiBase();
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/api/admin/home/hero-media`, formData, { withCredentials: true });
      }
      toast.success("Média(s) d’accueil importé(s).");
      if (inputRef.current) inputRef.current.value = "";
      await fetchMedia();
    } catch {
      toast.error("L’upload des médias d’accueil a échoué.");
    }
  };

  const handleDelete = async () => {
    if (!activeSlide || activeSlide.locked) return;
    const confirmed = window.confirm("Supprimer le média actuellement affiché ?");
    if (!confirmed) return;
    const API = getApiBase();
    await axios.delete(`${API}/api/admin/home/hero-media/${activeSlide.id}`, { withCredentials: true });
    toast.success("Média d’accueil supprimé.");
    setActiveIndex(0);
    await fetchMedia();
  };

  return (
    <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-home-hero-media-card">
      <CardContent className="p-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Carrousel Hero de l’accueil</div>
            <p className="mt-2 text-sm leading-7 text-slate-600">Le premier slide reste verrouillé : il s’agit toujours du diagramme animé actuel de la homepage.</p>
          </div>
          {activeSlide?.locked ? <Badge className="border border-[#b7e4c7] bg-[#ecfdf5] text-[#0f766e]" data-testid="admin-home-hero-locked-badge">Slide principal verrouillé</Badge> : null}
        </div>

        <div className="mt-6" data-testid="admin-home-hero-carousel-wrapper">
          <Carousel className="mx-auto max-w-[1200px]" opts={{ loop: slides.length > 1 }} setApi={setCarouselApi}>
            <CarouselContent className="-ml-0">
              {slides.map((slide) => (
                <CarouselItem className="pl-0" key={slide.id}>
                  {slide.locked ? <BimInfographic /> : <HomeHeroMediaSlide item={slide} />}
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {slides.length > 1 ? (
            <div className="mt-6 flex items-center justify-between gap-4" data-testid="admin-home-hero-carousel-navigation">
              <div className="flex items-center gap-2" data-testid="admin-home-hero-carousel-dots">
                {slides.map((slide, index) => (
                  <button
                    className={`h-2.5 rounded-full transition-all ${activeIndex === index ? "w-10 bg-[#0f766e]" : "w-2.5 bg-[#0f766e]/25 hover:bg-[#0f766e]/45"}`}
                    data-testid={`admin-home-hero-dot-${index + 1}`}
                    key={slide.id}
                    onClick={() => carouselApi?.scrollTo(index)}
                    type="button"
                  />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Button className="rounded-full border-[#d9e7d5] bg-white/80 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-home-hero-prev-button" onClick={() => carouselApi?.scrollPrev()} type="button" variant="outline">
                  Précédent
                </Button>
                <Button className="rounded-full border-[#d9e7d5] bg-white/80 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-home-hero-next-button" onClick={() => carouselApi?.scrollNext()} type="button" variant="outline">
                  Suivant
                </Button>
              </div>
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center justify-center gap-4" data-testid="admin-home-hero-actions">
            <input
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              data-testid="admin-home-hero-upload-input"
              multiple
              onChange={(e) => handleUpload(Array.from(e.target.files || []))}
              ref={inputRef}
              type="file"
            />
            <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-home-hero-upload-button" onClick={() => inputRef.current?.click()} type="button">
              Upload
            </Button>
            <Button className="rounded-full border-[#d8ece4] bg-white/90 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e] disabled:opacity-50" data-testid="admin-home-hero-delete-button" disabled={!!activeSlide?.locked} onClick={handleDelete} type="button" variant="outline">
              Delete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};